import { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { FaWhatsapp, FaTelegram } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n';
import { Sidebar } from './Sidebar';
import { toast } from '../common/Notifier';
import { GlossaryModal } from '../common/GlossaryModal';
import { BUILD_DATE } from "../../constants/version";

export const Layout = () => {
  const { user, athlete, isGuest, logout } = useAuth();
  const { t, language, changeLanguage } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  const homeLink = isGuest ? '/dashboard' : (user ? '/dashboard' : '/home');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const LANGUAGES = { en: 'EN', ru: 'RU', uk: 'UA' };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Бургер (мобильные) + Логотип */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900"
                aria-label="Menu"
              >
                <span className="text-xl">☰</span>
              </button>
                <Link to={homeLink} className="text-xl font-bold text-blue-600 flex items-center gap-2">
                  <img src="/moom1.png" alt="MoomDoom" className="w-12 h-12 object-contain" />
                  MoomDoom
                </Link>
            </div>

            {/* Правая часть */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex gap-1 bg-gray-50 p-1 rounded-lg">
                {Object.entries(LANGUAGES).map(([code, flag]) => (
                  <button
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className={`px-2 py-1 rounded text-xs transition-all ${
                      language === code ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                    }`}
                  >
                    {flag}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                {isGuest || user ? (
                    <>
                      <Link
                        to="/profile"
                        className="hidden sm:flex items-center gap-2"
                      >
                        {isGuest ? (
                          <span className="text-sm font-medium text-gray-700 bg-gray-200 px-3 py-1 rounded-full">
                            👤 GUEST
                          </span>
                        ) : (
                          <>
                            <img
                              src={athlete?.avatar_url || '/avatar-placeholder.png'}
                              alt=""
                              className="w-[60px] h-[60px] rounded-full object-cover border border-gray-200"
                            />
                            <span className="text-sm font-medium text-gray-700 bg-gray-200 px-3 py-1 rounded-full">
                              {user?.username || 'User'}
                            </span>
                          </>
                        )}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-sm text-red-500 hover:text-red-700 font-medium"
                      >
                        {t('common.logout')}
                      </button>
                    </>
                ) : (
                  <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                    {t('login.login_button')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex flex-1">
        {/* Sidebar — десктоп, всегда виден */}
        <aside className="hidden md:block w-64 shrink-0 bg-white border-r border-gray-200 p-4">
          <Sidebar />
        </aside>

        {/* Sidebar — мобильный drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <aside className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl p-4">
              <div className="flex justify-between items-center mb-4">
                <span className="font-bold text-blue-600">MoomDoom</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-500 hover:text-gray-800 text-xl"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <Sidebar onNavigate={() => setMobileMenuOpen(false)} />

              <div className="mt-6 pt-4 border-t border-gray-200 flex gap-1">
                {Object.entries(LANGUAGES).map(([code, flag]) => (
                  <button
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className={`px-2 py-1 rounded text-xs transition-all ${
                      language === code ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {flag}
                  </button>
                ))}
              </div>
            </aside>
          </div>
        )}

        <main className="flex-1 px-4 py-4 min-w-0">
          <Outlet />
        </main>
      </div>


    <footer className="relative bg-white border-t border-gray-200 py-2 z-30">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-xs text-gray-400">
          <button
            onClick={() => setGlossaryOpen(true)}
            className="text-gray-600 hover:text-gray-800 underline font-medium"
          >
            {t('glossary.open_link')}
          </button>

          <span>|</span>

          <div className="flex items-center gap-1">
            <a href="https://wa.me/380505777811" target="_blank" rel="noopener noreferrer" className="hover:text-green-500">
              <FaWhatsapp className="w-4 h-4" />
            </a>
            <a href="https://t.me/UngryUngryCat" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">
              <FaTelegram className="w-4 h-4" />
            </a>
          </div>

          <span className="hidden sm:inline">|</span>

            {/* Email текст с копированием */}
            <button
              onClick={async () => {
                try {
                  if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText('moomidoom@gmail.com');
                    toast.success(t('common.email_copied'));
                  } else {
                    throw new Error('Clipboard API unavailable');
                  }
                } catch {
                  window.location.href = 'mailto:moomidoom@gmail.com';
                }
              }}
              className="hidden sm:inline text-gray-600 hover:text-red-500 transition-colors"
            >
              moomidoom@gmail.com
            </button>
                  {/* Копирайт */}
          <span className="hidden lg:inline text-gray-600">
            © {new Date().getFullYear()} MoomDoom Inc. — A completely legitimate organization.
          </span>
        </div>
      </div>

      <span
        className="absolute bottom-1 right-2 text-[10px] text-gray-800 font-mono font-semibold"
        title={BUILD_DATE}
      >
        v.{BUILD_DATE}
      </span>
    </footer>

      <GlossaryModal open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
    </div>
  );
};